import { useState, useEffect, useCallback, useMemo } from 'react'
import { openDB } from 'idb'
import { useIdentity, useEngineManager, useRegistry, useWorkspaceSwitch, useWorkspaceMetadata } from '../../hooks/useWorkspace'
import { APP_BASE_PATH } from '../../config'
import { buildRuntimeDebugSnapshot } from '../../testing/runtimeDebugSnapshot'
import type { RuntimeDebugSnapshot } from '../../testing/runtimeDebugSnapshot'
import {
    clearRuntimeEventTrace,
    getRuntimeEventTrace,
    type RuntimeEventEntry,
} from '../../testing/runtimeEventTrace'
import {
    clearRecentBrowserHttpErrors,
    formatHttpError,
    MAX_RUNTIME_BROWSER_HTTP_ERRORS,
} from '@secureblob/sdk/browserHttpErrors'
import {
    clearRecentMcpToolCalls,
    getRecentMcpToolCalls,
    MAX_RUNTIME_MCP_TOOL_CALLS,
    type McpToolCallEntry,
} from '@secureblob/sdk/browserMcpTraffic'
import {
    getWorkspaceJobs,
    isTerminalComponentRunStage,
    type ComponentRunStatusRecord,
} from '@secureblob/sdk/componentJobs'
import { createComponentJobsClientOptions } from '../componentJobs/apiClient'
import {
    isRunningComponentJobStatus,
    isActiveComponentJobStatus,
} from '../componentJobs/jobHistory'
import { timeAgo } from '../../utils/writerUtils'
import './DebugPage.css'

// ── Helpers ──────────────────────────────────────────────────────

function toHex(buf: ArrayBuffer | Uint8Array | undefined | null): string {
    if (!buf) return '(null)'
    const arr = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
    return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
}

function jsonBlock(data: unknown): string {
    try {
        return JSON.stringify(data, (_k, v) => {
            if (v instanceof ArrayBuffer) return `[ArrayBuffer ${v.byteLength}B]`
            if (v instanceof Uint8Array) return toHex(v)
            return v
        }, 2)
    } catch {
        return String(data)
    }
}

function Section({ title, badge, badgeType, children, defaultOpen = false }: {
    title: string
    badge?: string | number
    badgeType?: 'warn' | 'empty'
    children: React.ReactNode
    defaultOpen?: boolean
}) {
    return (
        <details className="debug-section" open={defaultOpen}>
            <summary>
                {title}
                {badge !== undefined && (
                    <span className={`debug-badge ${badgeType ? `debug-badge--${badgeType}` : ''}`}>
                        {badge}
                    </span>
                )}
            </summary>
            <div className="debug-section-body">{children}</div>
        </details>
    )
}

function useRuntimeDebugSnapshotData() {
    const engine = useEngineManager()
    const { entries } = useRegistry()
    const { syncStates } = useWorkspaceSwitch()
    const { hasIdentity, identity, vaultExists, vaultUnlockMethod } = useIdentity()
    const [snapshot, setSnapshot] = useState<RuntimeDebugSnapshot | null>(null)
    const [error, setError] = useState<string | null>(null)

    const refresh = useCallback(async () => {
        try {
            const nextSnapshot = await buildRuntimeDebugSnapshot(
                engine,
                new Map(Object.entries(syncStates)),
            )
            setSnapshot(nextSnapshot)
            setError(null)
        } catch (nextError) {
            setSnapshot(null)
            setError(String(nextError))
        }
    }, [engine, syncStates])

    useEffect(() => {
        let disposed = false

        const refreshSnapshot = async () => {
            try {
                const nextSnapshot = await buildRuntimeDebugSnapshot(
                    engine,
                    new Map(Object.entries(syncStates)),
                )
                if (disposed) return
                setSnapshot(nextSnapshot)
                setError(null)
            } catch (nextError) {
                if (disposed) return
                setSnapshot(null)
                setError(String(nextError))
            }
        }

        void refreshSnapshot()

        const pollId = window.setInterval(() => {
            void refreshSnapshot()
        }, 1500)
        const handleBrowserStateChange = () => {
            void refreshSnapshot()
        }

        window.addEventListener('online', handleBrowserStateChange)
        window.addEventListener('offline', handleBrowserStateChange)
        window.addEventListener('storage', handleBrowserStateChange)

        return () => {
            disposed = true
            window.clearInterval(pollId)
            window.removeEventListener('online', handleBrowserStateChange)
            window.removeEventListener('offline', handleBrowserStateChange)
            window.removeEventListener('storage', handleBrowserStateChange)
        }
    }, [
        engine,
        entries,
        hasIdentity,
        identity?.emoji,
        identity?.kid,
        identity?.name,
        syncStates,
        vaultExists,
        vaultUnlockMethod,
    ])

    return {
        snapshot,
        error,
        refresh,
    }
}

function RuntimeSnapshotSection({
    snapshot,
    error,
}: {
    snapshot: RuntimeDebugSnapshot | null
    error: string | null
}) {
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')
    const [textCopyStatus, setTextCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')

    const badge = error
        ? 'ERROR'
        : snapshot
            ? snapshot.uxState
            : 'LOADING'

    const copyJson = useCallback(async () => {
        if (!snapshot) return
        try {
            await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2))
            setCopyStatus('copied')
            setTimeout(() => setCopyStatus('idle'), 2000)
        } catch {
            setCopyStatus('error')
            setTimeout(() => setCopyStatus('idle'), 2000)
        }
    }, [snapshot])

    const downloadJson = useCallback(() => {
        if (!snapshot) return
        const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `debug-snapshot-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }, [snapshot])

    const formatSnapshotAsText = (snap: RuntimeDebugSnapshot): string => {
        const lines: string[] = [`Runtime Snapshot — ${snap.capturedAt}`, '=' .repeat(50)]
        lines.push(`url: ${snap.url}`)
        lines.push(`uxState: ${snap.uxState}`)
        lines.push(`online: ${snap.online}`)
        lines.push(`isPlaywright: ${snap.isPlaywright}`)
        lines.push(`ephemeralMode: ${snap.ephemeralMode}`)
        lines.push(`localServerMode: ${snap.localServerMode}`)
        if (snap.identity) {
            lines.push('')
            lines.push('[Identity]')
            lines.push(`  hasIdentity: ${snap.identity.hasIdentity}`)
            lines.push(`  vaultExists: ${snap.identity.vaultExists}`)
            lines.push(`  vaultUnlockMethod: ${snap.identity.vaultUnlockMethod}`)
            lines.push(`  displayName: ${snap.identity.displayName ?? '(null)'}`)
            lines.push(`  emoji: ${snap.identity.emoji ?? '(null)'}`)
        }
        if (snap.workspaceReadiness) {
            lines.push('')
            lines.push('[Workspace Readiness]')
            const wr = snap.workspaceReadiness
            lines.push(`  blobId: ${wr.blobId}`)
            lines.push(`  canWrite: ${wr.canWrite}`)
            lines.push(`  syncing: ${wr.syncing}`)
            lines.push(`  synced: ${wr.synced}`)
            lines.push(`  online: ${wr.online}`)
            lines.push(`  connectivity: ${wr.connectivity}`)
            lines.push(`  pendingChanges: ${wr.pendingChanges}`)
            lines.push(`  lastSyncError: ${wr.lastSyncError ?? '(null)'}`)
            lines.push(`  localHead: ${wr.localHead}`)
            lines.push(`  remoteHead: ${wr.remoteHead}`)
            if (wr.conflictPaths.length > 0) {
                lines.push(`  conflictPaths: ${wr.conflictPaths.join(', ')}`)
            }
        }
        if (snap.activeWorkspace) {
            lines.push('')
            lines.push('[Active Workspace]')
            lines.push(`  blobId: ${snap.activeWorkspace.blobId}`)
            lines.push(`  label: ${snap.activeWorkspace.label}`)
            lines.push(`  emoji: ${snap.activeWorkspace.emoji}`)
            lines.push(`  canWrite: ${snap.activeWorkspace.canWrite}`)
        }
        if (snap.suspendedWorkspaces.length > 0) {
            lines.push('')
            lines.push(`[Suspended Workspaces: ${snap.suspendedWorkspaces.length}]`)
            for (const ws of snap.suspendedWorkspaces) {
                lines.push(`  - ${ws.blobId} (${ws.label})`)
            }
        }
        if (snap.registry.length > 0) {
            lines.push('')
            lines.push(`[Registry: ${snap.registry.length} entries]`)
            for (const r of snap.registry) {
                lines.push(`  ${r.blobId} | ${r.label} | canWrite=${r.canWrite}`)
            }
        }
        if (snap.mcpConnections.length > 0) {
            lines.push('')
            lines.push(`[MCP Connections: ${snap.mcpConnections.length}]`)
            for (const c of snap.mcpConnections) {
                lines.push(`  ${c.label} | ${c.serverUrl} | connected=${c.connected}`)
            }
        }
        if (snap.httpErrors.length > 0) {
            lines.push('')
            lines.push(`[HTTP Errors: ${snap.httpErrors.length}]`)
            for (const e of snap.httpErrors) {
                lines.push(`  ${e.status} ${e.url} (${e.capturedAt?.split('T')[1]?.slice(0, 8) ?? '?'})`)
            }
        }
        if (snap.workspaceDiscovery) {
            lines.push('')
            lines.push('[Workspace Discovery]')
            lines.push(`  activeWorkspaceBlobId: ${snap.workspaceDiscovery.activeWorkspaceBlobId ?? '(null)'}`)
            lines.push(`  pendingWorkspaceBlobId: ${snap.workspaceDiscovery.pendingWorkspaceBlobId ?? '(null)'}`)
            if (snap.workspaceDiscovery.guestCapBlobIds.length > 0) {
                lines.push(`  guestCapBlobIds: ${snap.workspaceDiscovery.guestCapBlobIds.join(', ')}`)
            }
        }
        lines.push('')
        lines.push(`[Storage]`)
        lines.push(`  localStorage: ${snap.storage.localStorageKeys.length} keys`)
        lines.push(`  sessionStorage: ${snap.storage.sessionStorageKeys.length} keys`)
        lines.push(`  indexedDbNames: ${snap.storage.indexedDbNames.join(', ')}`)
        return lines.join('\n')
    }

    const copyText = useCallback(async () => {
        if (!snapshot) return
        try {
            await navigator.clipboard.writeText(formatSnapshotAsText(snapshot))
            setTextCopyStatus('copied')
            setTimeout(() => setTextCopyStatus('idle'), 2000)
        } catch {
            setTextCopyStatus('error')
            setTimeout(() => setTextCopyStatus('idle'), 2000)
        }
    }, [snapshot])

    return (
        <Section title="Runtime Snapshot" badge={badge} badgeType={error ? 'warn' : undefined} defaultOpen>
            {error ? (
                <p>Failed to build runtime snapshot: {error}</p>
            ) : snapshot ? (
                <>
                    <div className="debug-snapshot-toolbar" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                            className="debug-delete-btn"
                            onClick={copyJson}
                            disabled={copyStatus === 'copied'}
                            title="Copy snapshot JSON to clipboard"
                        >
                            {copyStatus === 'copied' ? 'Copied JSON!' : copyStatus === 'error' ? 'Copy failed' : 'Copy JSON'}
                        </button>
                        <button
                            className="debug-delete-btn"
                            onClick={downloadJson}
                            title="Download snapshot as JSON file"
                        >
                            Download .json
                        </button>
                        <button
                            className="debug-delete-btn"
                            onClick={copyText}
                            disabled={textCopyStatus === 'copied'}
                            title="Copy snapshot as formatted text for bug reports"
                        >
                            {textCopyStatus === 'copied' ? 'Copied text!' : textCopyStatus === 'error' ? 'Copy failed' : 'Copy as text'}
                        </button>
                    </div>
                    <pre className="debug-json">{jsonBlock(snapshot)}</pre>
                </>
            ) : (
                <p className="debug-loading">Loading…</p>
            )}
        </Section>
    )
}

function BrowserHttpErrorsSection({
    entries,
    onRefresh,
}: {
    entries: RuntimeDebugSnapshot['httpErrors']
    onRefresh: () => Promise<void>
}) {
    const handleClear = async () => {
        clearRecentBrowserHttpErrors()
        await onRefresh()
    }

    return (
        <Section
            title="Browser HTTP Errors"
            badge={entries.length > 0 ? `${entries.length}/${MAX_RUNTIME_BROWSER_HTTP_ERRORS}` : 'CLEAN'}
            badgeType={entries.length > 0 ? 'warn' : 'empty'}
            defaultOpen={entries.length > 0}
        >
            {entries.length === 0 ? (
                <p>No unexpected SecureBlob-owned HTTP 4xx/5xx responses recorded in this tab.</p>
            ) : (
                <>
                    <p className="debug-label" style={{ marginBottom: '0.5rem' }}>
                        Bounded live buffer of redacted unexpected SecureBlob fetch failures.
                    </p>
                    <table className="debug-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Status</th>
                                <th>Request</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => (
                                <tr key={`${entry.capturedAt ?? 'unknown'}-${entry.url}-${entry.status}-${index}`}>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {entry.capturedAt?.split('T')[1]?.slice(0, 8) ?? '—'}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {entry.status}
                                        {entry.statusText ? ` ${entry.statusText}` : ''}
                                    </td>
                                    <td>
                                        <code>{formatHttpError(entry)}</code>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ marginTop: '0.75rem' }}>
                        <button className="debug-delete-btn" onClick={() => void handleClear()}>
                            Clear HTTP error buffer
                        </button>
                    </div>
                </>
            )}
        </Section>
    )
}

// ── §159. MCP Tool Calls ────────────────────────────────────────

function McpToolCallsSection() {
    const [entries, setEntries] = useState<readonly McpToolCallEntry[]>([])
    const [tick, setTick] = useState(0)

    useEffect(() => {
        const interval = window.setInterval(() => {
            setEntries(getRecentMcpToolCalls())
        }, 2000)
        // Initial read
        setEntries(getRecentMcpToolCalls())
        return () => window.clearInterval(interval)
    }, [tick])

    const handleClear = () => {
        clearRecentMcpToolCalls()
        setEntries([])
        setTick(t => t + 1)
    }

    const handleRefresh = () => setTick(t => t + 1)

    return (
        <Section
            title="MCP Tool Calls"
            badge={entries.length > 0 ? `${entries.length}/${MAX_RUNTIME_MCP_TOOL_CALLS}` : 'CLEAN'}
            badgeType={entries.length > 0 ? 'warn' : 'empty'}
            defaultOpen={entries.length > 0}
        >
            {entries.length === 0 ? (
                <p>No MCP tool calls recorded in this tab. Calls are recorded as the agent makes MCP requests.</p>
            ) : (
                <>
                    <p className="debug-label" style={{ marginBottom: '0.5rem' }}>
                        Bounded live buffer of recent MCP JSON-RPC tool calls from <code>RemoteMcpClient</code>.
                    </p>
                    <table className="debug-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Method</th>
                                <th>Connection</th>
                                <th>Duration</th>
                                <th>Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => (
                                <tr key={`${entry.callId}-${index}`}>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {entry.capturedAt?.split('T')[1]?.slice(0, 8) ?? '—'}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <code>{entry.method}</code>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {entry.connectionLabel}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {entry.durationMs}ms
                                    </td>
                                    <td>
                                        {entry.isError
                                            ? <span style={{ color: 'var(--color-error)' }}>✕ {entry.redactedResult ?? 'error'}</span>
                                            : entry.redactedResult
                                                ? <span style={{ color: 'var(--color-text-secondary)' }}>{entry.redactedResult}</span>
                                                : <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button onClick={handleRefresh} style={{ padding: '2px 8px', fontSize: '0.75rem' }}>↻ Refresh</button>
                        <button className="debug-delete-btn" onClick={handleClear}>
                            Clear MCP tool call buffer
                        </button>
                    </div>
                </>
            )}
        </Section>
    )
}

// ── 1a. Runtime Event Trace (§145) ──────────────────────────────

function kindColor(kind: string): string {
    if (kind === 'identity:changed') return '#a78bfa'
    if (kind === 'workspace:switched') return '#60a5fa'
    if (kind === 'workspace:sync-status') return '#34d399'
    if (kind === 'workspace:deleted') return '#f87171'
    if (kind === 'registry:sync-status') return '#fbbf24'
    if (kind === 'sqlite3mt:debug-stage') return '#f97316'
    return 'inherit'
}

function RuntimeEventTraceSection() {
    const [events, setEvents] = useState<RuntimeEventEntry[]>([])

    useEffect(() => {
        const interval = window.setInterval(() => {
            setEvents(getRuntimeEventTrace())
        }, 800)
        return () => window.clearInterval(interval)
    }, [])

    useEffect(() => {
        setEvents(getRuntimeEventTrace())
    }, [])

    const handleClear = () => {
        clearRuntimeEventTrace()
        setEvents([])
    }

    const handleRefresh = () => {
        setEvents(getRuntimeEventTrace())
    }

    return (
        <Section title="Runtime Event Trace" badge={`${events.length} events`} defaultOpen>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={handleRefresh} style={{ padding: '2px 8px', fontSize: '0.75rem' }}>↻ Refresh</button>
                <button onClick={handleClear} style={{ padding: '2px 8px', fontSize: '0.75rem', color: 'var(--color-error)' }}>🗑 Clear</button>
            </div>
            {events.length === 0 ? (
                <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>No events yet. Events are recorded as identity, workspace, and sqlite3mt state changes occur.</p>
            ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {events.map((entry) => (
                        <div key={entry.id} style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 120px 1fr',
                            gap: '0.5rem',
                            padding: '3px 0',
                            borderBottom: '1px solid var(--color-border)',
                            fontSize: '0.75rem',
                            alignItems: 'start',
                        }}>
                            <span style={{ color: 'var(--color-text-tertiary)', fontFamily: 'monospace', flexShrink: 0 }}>
                                {entry.timestamp.split('T')[1]?.slice(0, 8) ?? entry.timestamp}
                            </span>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: kindColor(entry.kind) }}>
                                {entry.kind}
                            </span>
                            <span style={{ fontFamily: 'monospace', color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>
                                {JSON.stringify(entry.data).slice(0, 120)}
                                {JSON.stringify(entry.data).length > 120 ? '…' : ''}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    )
}

// ── 1. Identity Vault (raw IDB read) ────────────────────────────

function IdentityVaultSection() {
    const [record, setRecord] = useState<Record<string, unknown> | null | 'loading'>('loading')
    const [tick, setTick] = useState(0)

    useEffect(() => {
        (async () => {
            try {
                const db = await openDB('secureblob-identity-vault-v1', 1)
                const row = await db.get('identity_vault', 'default')
                db.close()
                setRecord(row?.record ? { ...row.record } : null)
            } catch {
                setRecord(null)
            }
        })()
    }, [tick])

    const handleDelete = useCallback(async () => {
        if (!confirm('Delete identity vault? This will destroy your local identity.')) return
        try {
            const db = await openDB('secureblob-identity-vault-v1', 1)
            await db.delete('identity_vault', 'default')
            db.close()
            setTick(t => t + 1)
        } catch (e) { alert(String(e)) }
    }, [])

    if (record === 'loading') return <Section title="Identity Vault"><p className="debug-loading">Loading…</p></Section>

    return (
        <Section title="Identity Vault" badge={record ? 'EXISTS' : 'EMPTY'} badgeType={record ? undefined : 'empty'}>
            {record ? (
                <>
                    <pre className="debug-json">{jsonBlock({
                        unlockMethod: (record as any).unlockMethod,
                        credentialId: toHex((record as any).credentialId),
                        prfSalt: toHex((record as any).prfSalt),
                        keyWrapSalt: `[${((record as any).keyWrapSalt as ArrayBuffer)?.byteLength ?? 0}B]`,
                        keyWrapIv: `[${((record as any).keyWrapIv as ArrayBuffer)?.byteLength ?? 0}B]`,
                        vaultKeyCiphertext: `[${((record as any).vaultKeyCiphertext as ArrayBuffer)?.byteLength ?? 0}B]`,
                        identityCiphertext: `[${((record as any).identityCiphertext as ArrayBuffer)?.byteLength ?? 0}B]`,
                        identityIv: `[${((record as any).identityIv as ArrayBuffer)?.byteLength ?? 0}B]`,
                        createdAt: (record as any).createdAt,
                        updatedAt: (record as any).updatedAt,
                    })}</pre>
                    <button className="debug-delete-btn" onClick={handleDelete}>🗑 Delete vault record</button>
                </>
            ) : <p>No vault record in IndexedDB.</p>}
        </Section>
    )
}

// ── 2. Identity State (from hooks) ──────────────────────────────

function IdentityStateSection() {
    const { hasIdentity, identity, vaultExists, vaultUnlockMethod } = useIdentity()

    return (
        <Section title="Identity State" badge={hasIdentity ? 'UNLOCKED' : vaultExists ? 'LOCKED' : 'NONE'}
            badgeType={hasIdentity ? undefined : 'warn'}>
            <dl className="debug-kv">
                <dt>hasIdentity</dt><dd>{String(hasIdentity)}</dd>
                <dt>vaultExists</dt><dd>{String(vaultExists)}</dd>
                <dt>vaultUnlockMethod</dt><dd>{String(vaultUnlockMethod ?? '(null)')}</dd>
            </dl>
            {identity && (
                <pre className="debug-json">{jsonBlock(identity)}</pre>
            )}
        </Section>
    )
}

// ── 3. Registry ─────────────────────────────────────────────────

function RegistrySection() {
    const { hasIdentity } = useIdentity()
    const engine = useEngineManager()

    if (!hasIdentity) return (
        <Section title="Registry" badge="NOT SIGNED IN" badgeType="empty">
            <p>Sign in to view workspace registry.</p>
        </Section>
    )

    const entries = engine.getRegistry()

    return (
        <Section title="Registry" badge={`${entries.length} entries`}>
            {entries.length === 0 ? <p>No workspace entries.</p> : (
                <table className="debug-table">
                    <thead><tr>
                        <th>Label</th><th>blobId</th><th>Emoji</th><th>Capability</th><th>Permissions</th>
                    </tr></thead>
                    <tbody>
                        {entries.map(e => (
                            <tr key={e.blobId}>
                                <td>{e.label}</td>
                                <td>{e.blobId}</td>
                                <td>{e.emoji || '—'}</td>
                                <td style={{ maxWidth: 200 }}>{(e as any).capability || '—'}</td>
                                <td>{(e as any).permissions || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <pre className="debug-json">{jsonBlock(entries)}</pre>
        </Section>
    )
}

// ── 4. Tombstones ───────────────────────────────────────────────

function TombstoneSection() {
    const { hasIdentity, identity } = useIdentity()
    const [tick, setTick] = useState(0)

    const kid = identity?.kid
    const key = kid ? `sb:tombstones:${kid}` : null
    let tombstones: string[] = []
    try {
        if (key) tombstones = JSON.parse(localStorage.getItem(key) || '[]')
    } catch { /* */ }

    const handleRemove = (blobId: string) => {
        if (!key) return
        const next = tombstones.filter(id => id !== blobId)
        localStorage.setItem(key, JSON.stringify(next))
        setTick(t => t + 1)
    }

    const handleClearAll = () => {
        if (!key || !confirm('Clear all tombstones?')) return
        localStorage.removeItem(key)
        setTick(t => t + 1)
    }

    void tick // force re-render

    if (!hasIdentity) return (
        <Section title="Tombstones" badge="NOT SIGNED IN" badgeType="empty">
            <p>Sign in to view tombstones.</p>
        </Section>
    )

    return (
        <Section title="Tombstones" badge={`${tombstones.length}`} badgeType={tombstones.length === 0 ? 'empty' : 'warn'}>
            {tombstones.length === 0 ? <p>No tombstoned workspace IDs.</p> : (
                <>
                    <table className="debug-table">
                        <thead><tr><th>blobId</th><th></th></tr></thead>
                        <tbody>
                            {tombstones.map(id => (
                                <tr key={id}>
                                    <td>{id}</td>
                                    <td><button className="debug-delete-btn-sm" onClick={() => handleRemove(id)}>✕</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button className="debug-delete-btn" onClick={handleClearAll}>🗑 Clear all tombstones</button>
                </>
            )}
        </Section>
    )
}

// ── 5. MCP Connections ──────────────────────────────────────────

function McpConnectionsSection() {
    const { hasIdentity } = useIdentity()
    const engine = useEngineManager()

    if (!hasIdentity) return (
        <Section title="MCP Connections" badge="NOT SIGNED IN" badgeType="empty">
            <p>Sign in to view MCP connections.</p>
        </Section>
    )

    const connections = engine.getMcpConnections()

    return (
        <Section title="MCP Connections" badge={`${connections.length}`}>
            {connections.length === 0 ? <p>No MCP connections registered.</p> : (
                <pre className="debug-json">{jsonBlock(connections)}</pre>
            )}
        </Section>
    )
}

// ── 6. localStorage ─────────────────────────────────────────────

function LocalStorageSection() {
    const [tick, setTick] = useState(0)

    const entries: [string, string][] = []
    for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)!
        entries.push([k, localStorage.getItem(k)!])
    }
    entries.sort((a, b) => a[0].localeCompare(b[0]))

    void tick

    const handleDelete = (key: string) => {
        localStorage.removeItem(key)
        setTick(t => t + 1)
    }

    const handleClearAll = () => {
        if (!confirm('Clear ALL localStorage? This will destroy registry cache, tombstones, theme, etc.')) return
        localStorage.clear()
        setTick(t => t + 1)
    }

    return (
        <Section title="localStorage" badge={`${entries.length} keys`}>
            {entries.length === 0 ? <p>Empty.</p> : (
                <table className="debug-table">
                    <thead><tr><th>Key</th><th>Value</th><th></th></tr></thead>
                    <tbody>
                        {entries.map(([k, v]) => (
                            <tr key={k}>
                                <td style={{ whiteSpace: 'nowrap' }}>{k}</td>
                                <td><pre className="debug-json" style={{ maxHeight: 150, margin: 0 }}>{tryPrettyJson(v)}</pre></td>
                                <td><button className="debug-delete-btn-sm" onClick={() => handleDelete(k)}>✕</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <button className="debug-delete-btn" onClick={handleClearAll}>🗑 Clear all localStorage</button>
        </Section>
    )
}

// ── 7. sessionStorage ───────────────────────────────────────────

function SessionStorageSection() {
    const [tick, setTick] = useState(0)

    const entries: [string, string][] = []
    for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i)!
        entries.push([k, sessionStorage.getItem(k)!])
    }
    entries.sort((a, b) => a[0].localeCompare(b[0]))

    void tick

    const handleDelete = (key: string) => {
        sessionStorage.removeItem(key)
        setTick(t => t + 1)
    }

    const handleClearAll = () => {
        if (!confirm('Clear ALL sessionStorage?')) return
        sessionStorage.clear()
        setTick(t => t + 1)
    }

    return (
        <Section title="sessionStorage" badge={`${entries.length} keys`}>
            {entries.length === 0 ? <p>Empty.</p> : (
                <table className="debug-table">
                    <thead><tr><th>Key</th><th>Value</th><th></th></tr></thead>
                    <tbody>
                        {entries.map(([k, v]) => (
                            <tr key={k}>
                                <td style={{ whiteSpace: 'nowrap' }}>{k}</td>
                                <td><pre className="debug-json" style={{ maxHeight: 150, margin: 0 }}>{tryPrettyJson(v)}</pre></td>
                                <td><button className="debug-delete-btn-sm" onClick={() => handleDelete(k)}>✕</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <button className="debug-delete-btn" onClick={handleClearAll}>🗑 Clear all sessionStorage</button>
        </Section>
    )
}

// ── 8. IndexedDB Overview ───────────────────────────────────────

interface IdbInfo {
    name: string
    version: number
    stores: { name: string; count: number }[]
}

function IndexedDbSection() {
    const [databases, setDatabases] = useState<IdbInfo[] | 'loading' | 'unsupported'>('loading')
    const [tick, setTick] = useState(0)

    useEffect(() => {
        (async () => {
            if (!indexedDB.databases) {
                setDatabases('unsupported')
                return
            }
            try {
                const dbs = await indexedDB.databases()
                const result: IdbInfo[] = []
                for (const dbMeta of dbs) {
                    if (!dbMeta.name) continue
                    try {
                        const db = await openDB(dbMeta.name, dbMeta.version)
                        const stores: { name: string; count: number }[] = []
                        for (const storeName of db.objectStoreNames) {
                            try {
                                const count = await db.count(storeName)
                                stores.push({ name: storeName, count })
                            } catch {
                                stores.push({ name: storeName, count: -1 })
                            }
                        }
                        db.close()
                        result.push({ name: dbMeta.name, version: dbMeta.version ?? 0, stores })
                    } catch {
                        result.push({ name: dbMeta.name, version: dbMeta.version ?? 0, stores: [] })
                    }
                }
                setDatabases(result)
            } catch {
                setDatabases('unsupported')
            }
        })()
    }, [tick])

    const handleDeleteDb = async (name: string) => {
        if (!confirm(`Delete IndexedDB "${name}"? This is destructive.`)) return
        try {
            // Close any open connections first
            const db = await openDB(name)
            db.close()
        } catch { /* */ }
        try {
            await new Promise<void>((resolve, reject) => {
                const req = indexedDB.deleteDatabase(name)
                req.onsuccess = () => resolve()
                req.onerror = () => reject(req.error)
            })
            setTick(t => t + 1)
        } catch (e) { alert(String(e)) }
    }

    if (databases === 'loading') return <Section title="IndexedDB"><p className="debug-loading">Loading…</p></Section>
    if (databases === 'unsupported') return <Section title="IndexedDB"><p>indexedDB.databases() not supported in this browser.</p></Section>

    return (
        <Section title="IndexedDB" badge={`${databases.length} databases`}>
            {databases.map(db => (
                <div key={db.name} style={{ marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <strong>{db.name}</strong>
                        <span className="debug-badge">v{db.version}</span>
                        <button className="debug-delete-btn-sm" onClick={() => handleDeleteDb(db.name)} title="Delete database">🗑</button>
                    </div>
                    {db.stores.length > 0 && (
                        <table className="debug-table" style={{ marginTop: '0.25rem' }}>
                            <thead><tr><th>Object Store</th><th>Records</th></tr></thead>
                            <tbody>
                                {db.stores.map(s => (
                                    <tr key={s.name}>
                                        <td>{s.name}</td>
                                        <td>{s.count >= 0 ? s.count : '?'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ))}
        </Section>
    )
}

// ── 9. Service Worker ───────────────────────────────────────────

function ServiceWorkerSection() {
    const [swInfo, setSwInfo] = useState<Record<string, unknown> | null>(null)

    useEffect(() => {
        (async () => {
            if (!('serviceWorker' in navigator)) {
                setSwInfo({ supported: false })
                return
            }
            try {
                const reg = await navigator.serviceWorker.getRegistration()
                if (!reg) {
                    setSwInfo({ supported: true, registered: false })
                    return
                }
                setSwInfo({
                    supported: true,
                    registered: true,
                    scope: reg.scope,
                    active: reg.active ? { state: reg.active.state, scriptURL: reg.active.scriptURL } : null,
                    installing: reg.installing ? { state: reg.installing.state, scriptURL: reg.installing.scriptURL } : null,
                    waiting: reg.waiting ? { state: reg.waiting.state, scriptURL: reg.waiting.scriptURL } : null,
                    updateViaCache: reg.updateViaCache,
                })
            } catch (e) {
                setSwInfo({ supported: true, error: String(e) })
            }
        })()
    }, [])

    const handleUnregister = async () => {
        if (!confirm('Unregister service worker?')) return
        try {
            const reg = await navigator.serviceWorker.getRegistration()
            if (reg) await reg.unregister()
            setSwInfo({ supported: true, registered: false, note: 'Unregistered. Reload page.' })
        } catch (e) { alert(String(e)) }
    }

    return (
        <Section title="Service Worker" badge={swInfo?.registered ? 'ACTIVE' : 'NONE'}
            badgeType={swInfo?.registered ? undefined : 'empty'}>
            {swInfo ? (
                <>
                    <pre className="debug-json">{jsonBlock(swInfo)}</pre>
                    {swInfo.registered && (
                        <button className="debug-delete-btn" onClick={handleUnregister}>🗑 Unregister SW</button>
                    )}
                </>
            ) : <p className="debug-loading">Loading…</p>}
        </Section>
    )
}

// ── 10. Env & Platform ──────────────────────────────────────────

function EnvSection() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as any).standalone === true
    const online = navigator.onLine

    return (
        <Section title="Environment & Platform">
            <dl className="debug-kv">
                <dt>User Agent</dt><dd>{navigator.userAgent}</dd>
                <dt>Platform</dt><dd>{navigator.platform}</dd>
                <dt>Language</dt><dd>{navigator.language}</dd>
                <dt>Online</dt><dd><span className={`debug-dot ${online ? 'debug-dot--green' : 'debug-dot--red'}`} />{String(online)}</dd>
                <dt>Standalone (PWA)</dt><dd>{String(isStandalone)}</dd>
                <dt>APP_BASE_PATH</dt><dd>{APP_BASE_PATH || '(empty)'}</dd>
                <dt>Location</dt><dd>{window.location.href}</dd>
                <dt>Origin</dt><dd>{window.location.origin}</dd>
                <dt>Cookie Enabled</dt><dd>{String(navigator.cookieEnabled)}</dd>
                <dt>DevicePixelRatio</dt><dd>{window.devicePixelRatio}</dd>
                <dt>Screen</dt><dd>{screen.width}×{screen.height} (avail: {screen.availWidth}×{screen.availHeight})</dd>
                <dt>Viewport</dt><dd>{window.innerWidth}×{window.innerHeight}</dd>
            </dl>
        </Section>
    )
}

// ── 11. Active Workspace ────────────────────────────────────────

function ActiveWorkspaceSection() {
    const { hasIdentity } = useIdentity()
    const engine = useEngineManager()

    if (!hasIdentity) return (
        <Section title="Active Workspace" badge="NOT SIGNED IN" badgeType="empty">
            <p>Sign in to view active workspace state.</p>
        </Section>
    )

    // Pull whatever state we can from the engine
    let info: Record<string, unknown> = {}
    try {
        const ws = engine.getActiveWorkspace?.()
        if (ws) {
            info = { ...ws }
        } else {
            info = { status: 'No active workspace' }
        }
    } catch {
        info = { status: 'Error reading workspace' }
    }

    return (
        <Section title="Active Workspace">
            <pre className="debug-json">{jsonBlock(info)}</pre>
        </Section>
    )
}

// ── Utility ─────────────────────────────────────────────────────

function tryPrettyJson(v: string): string {
    try {
        const parsed = JSON.parse(v)
        return JSON.stringify(parsed, null, 2)
    } catch {
        return v
    }
}

// ── 12. Suspended Workspaces ────────────────────────────────────

function SuspendedWorkspacesSection() {
    const { hasIdentity } = useIdentity()
    const engine = useEngineManager()

    if (!hasIdentity) return (
        <Section title="Suspended Workspaces" badge="NOT SIGNED IN" badgeType="empty">
            <p>Sign in to view suspended workspaces.</p>
        </Section>
    )

    let suspended: unknown[] = []
    try {
        suspended = engine.getSuspendedWorkspaces()
    } catch { /* */ }

    return (
        <Section title="Suspended Workspaces" badge={`${suspended.length}`}
            badgeType={suspended.length === 0 ? 'empty' : undefined}>
            {suspended.length === 0 ? <p>No suspended workspaces.</p> : (
                <pre className="debug-json">{jsonBlock(suspended)}</pre>
            )}
        </Section>
    )
}

// ── 13. Workspace Discovery ─────────────────────────────────────
// Parsed view of the sessionStorage keys used by the workspace init flow

function WorkspaceDiscoverySection() {
    const activeWs = sessionStorage.getItem('sb:active-workspace')
    const pendingWs = sessionStorage.getItem('sb:pending-workspace')

    // Collect all guest-cap entries
    const guestCaps: [string, string][] = []
    for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i)!
        if (k.startsWith('sb:guest-cap:')) {
            guestCaps.push([k, sessionStorage.getItem(k)!])
        }
    }

    const hasAny = activeWs || pendingWs || guestCaps.length > 0

    return (
        <Section title="Workspace Discovery" badge={hasAny ? `${guestCaps.length} guest caps` : 'CLEAN'}
            badgeType={hasAny ? undefined : 'empty'}>
            <dl className="debug-kv">
                <dt>Active Workspace</dt><dd>{activeWs || '(none)'}</dd>
                <dt>Pending Workspace</dt><dd>{pendingWs ? <pre className="debug-json" style={{ margin: 0 }}>{tryPrettyJson(pendingWs)}</pre> : '(none)'}</dd>
            </dl>
            {guestCaps.length > 0 && (
                <>
                    <p className="debug-label" style={{ marginTop: '0.5rem' }}>Guest Capabilities ({guestCaps.length})</p>
                    <table className="debug-table">
                        <thead><tr><th>blobId</th><th>Capability Data</th></tr></thead>
                        <tbody>
                            {guestCaps.map(([k, v]) => (
                                <tr key={k}>
                                    <td>{k.replace('sb:guest-cap:', '')}</td>
                                    <td><pre className="debug-json" style={{ maxHeight: 120, margin: 0 }}>{tryPrettyJson(v)}</pre></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </Section>
    )
}

// ── Component Jobs ───────────────────────────────────────────────

function ComponentJobsSection() {
    const { hasIdentity } = useIdentity()
    const { entries } = useRegistry()
    const { blobId: workspaceBlobId } = useWorkspaceMetadata()
    const [tick, setTick] = useState(0)

    const currentWorkspaceEntry = useMemo(
        () => entries.find(entry => entry.blobId === workspaceBlobId) ?? null,
        [entries, workspaceBlobId],
    )
    const componentJobsClient = useMemo(
        () => currentWorkspaceEntry ? createComponentJobsClientOptions(currentWorkspaceEntry) : null,
        [currentWorkspaceEntry],
    )

    const [jobsData, setJobsData] = useState<{
        activeRuns: ComponentRunStatusRecord[]
        recentRuns: ComponentRunStatusRecord[]
    } | null>(null)
    const [loading, setLoading] = useState(false)

    const loadJobs = useCallback(async () => {
        if (!componentJobsClient || !workspaceBlobId) return
        setLoading(true)
        try {
            const data = await getWorkspaceJobs({
                ...componentJobsClient,
                workspaceBlobId,
            })
            const activeRuns = data.runs.filter(run => isActiveComponentJobStatus(run.stage))
            const recentRuns = data.runs
                .filter(run => isTerminalComponentRunStage(run.stage))
                .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
                .slice(0, 5)
            setJobsData({ activeRuns, recentRuns })
        } catch {
            setJobsData(null)
        } finally {
            setLoading(false)
        }
    }, [componentJobsClient, workspaceBlobId])

    useEffect(() => {
        if (hasIdentity && componentJobsClient) {
            void loadJobs()
        }
    }, [hasIdentity, componentJobsClient, loadJobs, tick])

    const handleRefresh = () => setTick(t => t + 1)

    if (!hasIdentity) return (
        <Section title="Component Jobs" badge="NOT SIGNED IN" badgeType="empty" defaultOpen={false}>
            <p>Sign in to view component job status.</p>
        </Section>
    )

    if (!componentJobsClient) return (
        <Section title="Component Jobs" badge="NO WORKSPACE" badgeType="empty" defaultOpen={false}>
            <p>No active workspace to query component jobs.</p>
        </Section>
    )

    const totalActive = jobsData?.activeRuns.length ?? 0
    const totalRecent = jobsData?.recentRuns.length ?? 0

    return (
        <Section
            title="Component Jobs"
            badge={loading ? 'LOADING' : totalActive > 0 ? `${totalActive} active` : totalRecent > 0 ? `${totalRecent} recent` : 'CLEAN'}
            badgeType={totalActive > 0 ? 'warn' : totalRecent > 0 ? undefined : 'empty'}
            defaultOpen={false}
        >
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={handleRefresh} style={{ padding: '2px 8px', fontSize: '0.75rem' }}>↻ Refresh</button>
            </div>

            {!jobsData && !loading && (
                <p>No component jobs data available. Click Refresh to load.</p>
            )}

            {jobsData && jobsData.activeRuns.length === 0 && jobsData.recentRuns.length === 0 && (
                <p>No component jobs for this workspace.</p>
            )}

            {jobsData && jobsData.activeRuns.length > 0 && (
                <>
                    <p className="debug-label" style={{ marginBottom: '0.5rem' }}>Active Runs</p>
                    <table className="debug-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Stage</th>
                                <th>Status</th>
                                <th>Started</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobsData.activeRuns.map(run => (
                                <tr key={run.runId}>
                                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {run.sourcePath?.split('/').pop() ?? run.runId.slice(0, 8)}
                                    </td>
                                    <td>
                                        <span className={`debug-badge ${isRunningComponentJobStatus(run.stage) ? 'debug-badge--warn' : ''}`}>
                                            {run.stage}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{run.message || run.stage}</td>
                                    <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
                                        {timeAgo(new Date(run.startedAt).getTime(), { maxDepth: 'days' })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {jobsData && jobsData.recentRuns.length > 0 && (
                <>
                    <p className="debug-label" style={{ marginBottom: '0.5rem', marginTop: jobsData.activeRuns.length > 0 ? '0.75rem' : 0 }}>Recent Runs</p>
                    <table className="debug-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Result</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobsData.recentRuns.map(run => (
                                <tr key={run.runId}>
                                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {run.sourcePath?.split('/').pop() ?? run.runId.slice(0, 8)}
                                    </td>
                                    <td>
                                        <span style={{ color: run.stage === 'completed' ? 'var(--color-success, green)' : 'var(--color-error, red)' }}>
                                            {run.stage === 'completed' ? '✓ pass' : run.stage === 'failed' ? '✕ fail' : run.stage}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
                                        {run.totalElapsedSeconds >= 0
                                            ? run.totalElapsedSeconds < 60
                                                ? `${run.totalElapsedSeconds}s`
                                                : run.totalElapsedSeconds < 3600
                                                    ? `${Math.floor(run.totalElapsedSeconds / 60)}m`
                                                    : `${Math.floor(run.totalElapsedSeconds / 3600)}h ${Math.floor((run.totalElapsedSeconds % 3600) / 60)}m`
                                            : timeAgo(new Date(run.startedAt).getTime(), { maxDepth: 'days' })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </Section>
    )
}

// ── Nuclear Reset ───────────────────────────────────────────────

function NuclearResetSection() {
    const handleReset = async () => {
        if (!confirm('⚠️ FULL RESET: Delete ALL IndexedDB databases, localStorage, sessionStorage, and unregister service worker. This cannot be undone. Continue?')) return
        if (!confirm('Are you really sure? This will destroy your identity, all local data, and cached objects.')) return

        try {
            // 1. Clear storage
            localStorage.clear()
            sessionStorage.clear()

            // 2. Delete all IDB databases (must await each deletion!)
            if (indexedDB.databases) {
                const dbs = await indexedDB.databases()
                await Promise.all(dbs.map(db => {
                    if (!db.name) return Promise.resolve()
                    return new Promise<void>((resolve, reject) => {
                        const req = indexedDB.deleteDatabase(db.name!)
                        req.onsuccess = () => resolve()
                        req.onerror = () => reject(req.error)
                        req.onblocked = () => {
                            console.warn(`IDB "${db.name}" blocked — forcing resolve`)
                            resolve()
                        }
                    })
                }))
            }

            // 3. Unregister SW
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations()
                for (const r of regs) await r.unregister()
            }

            alert('Reset complete. Reloading…')
            window.location.reload()
        } catch (e) {
            alert('Error: ' + String(e))
        }
    }

    return (
        <Section title="☢️ Nuclear Reset" badge="DANGER" badgeType="warn">
            <p>Delete all local state: IndexedDB, localStorage, sessionStorage, and service worker.</p>
            <button className="debug-delete-btn debug-delete-btn--danger" onClick={handleReset}>
                ☢️ Full factory reset
            </button>
        </Section>
    )
}

// ── Main Page ───────────────────────────────────────────────────

export default function DebugPage() {
    const { snapshot, error, refresh } = useRuntimeDebugSnapshotData()

    return (
        <div className="debug-page">
            <div className="debug-page-header">
                <div>
                    <h1>🔧 SecureBlob Debug Inspector</h1>
                    <p className="debug-subtitle">
                        Shared runtime snapshot plus raw local inspector. {new Date().toISOString()}
                    </p>
                </div>
                <a className="debug-back-link" href={APP_BASE_PATH}>
                    ← Back to app
                </a>
            </div>

            <RuntimeSnapshotSection snapshot={snapshot} error={error} />
            <BrowserHttpErrorsSection entries={snapshot?.httpErrors ?? []} onRefresh={refresh} />
            <McpToolCallsSection />
            <RuntimeEventTraceSection />
            <IdentityVaultSection />
            <IdentityStateSection />
            <RegistrySection />
            <TombstoneSection />
            <McpConnectionsSection />
            <ActiveWorkspaceSection />
            <SuspendedWorkspacesSection />
            <WorkspaceDiscoverySection />
            <LocalStorageSection />
            <SessionStorageSection />
            <IndexedDbSection />
            <ServiceWorkerSection />
            <EnvSection />
            <ComponentJobsSection />
            <NuclearResetSection />
        </div>
    )
}
